import { forwardRef } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import './About.css';

const About = forwardRef((props, ref) => {
  const { theme } = useTheme();

  return (
    <section id="about" className={`about ${theme}`} ref={ref}>
      {/* Background Elements */}
      <div className="about-background">
        <div className="floating-shape shape-1"></div>
        <div className="floating-shape shape-2"></div>
        <div className="floating-shape shape-3"></div>
        <div className="grid-overlay"></div>
      </div>

      <div className="container">
        {/* Enhanced Section Title */}
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <motion.span 
            className="section-badge"
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Get to know me
          </motion.span>
          
          {/*<motion.h2 
            className="section-title"
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            About Me
            <motion.span 
              className="title-decoration"
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}cd
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.5 }}
            />
          </motion.h2>*/}
        </motion.div>
        
        <div className="about-content">
          {/* Text Content */}
          <motion.div 
            className="about-text"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {/* Introduction */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="intro-text"
            >
              <p className="intro-paragraph">
                Hello! I'm <span className="highlight">Chathushki Kumarage</span>, a passionate Software Engineer with a strong focus on Web Development and UI/UX Design. I love creating clean, responsive, and intuitive websites that deliver exceptional user experiences.
              </p>
            </motion.div>
            
            {/* Experience Text */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="experience-section"
            >
              <p className="experience-text">
                <span className="emoji">🔍</span>
                I'm always open to learning, growing, and collaborating on exciting tech projects. Driven by curiosity and a passion for improvement, I continuously explore new technologies to expand my skills and deliver better products.
              </p>
              <p className="collaboration-text">
                Let's build something amazing together
              </p>
            </motion.div>

            {/* Skills Highlight */}
            <motion.div 
              className="skills-highlight"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <h3 className="skills-title">What I Do</h3>
              <div className="skills-grid">
                <div className="skill-item">
                  <div className="skill-icon">
                    <i className="fas fa-code"></i>
                  </div>
                  <span>Frontend Development</span>
                </div>
                <div className="skill-item">
                  <div className="skill-icon">
                    <i className="fas fa-paint-brush"></i>
                  </div>
                  <span>UI/UX Design</span>
                </div>
                <div className="skill-item">
                  <div className="skill-icon">
                    <i className="fas fa-mobile-alt"></i>
                  </div>
                  <span>Responsive Design</span>
                </div>
                <div className="skill-item">
                  <div className="skill-icon">
                    <i className="fas fa-rocket"></i>
                  </div>
                  <span>Performance Optimization</span>
                </div>
              </div>
            </motion.div>
            
            {/* Personal Details */}
            <motion.div 
              className="about-details"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <h3 className="details-title">Contact Information</h3>
              
              <div className="details-grid">
                <motion.div 
                  className="detail-card glow-on-hover"
                  whileHover={{ y: -5 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="detail-icon pulse-animation">
                    <i className="fas fa-user"></i>
                  </div>
                  <div className="detail-content">
                    <h4>Full Name</h4>
                    <p className="detail-value">Chathushki Asmada Upulini Kumarage</p>
                    <div className="detail-underline"></div>
                  </div>
                </motion.div>
                
                <motion.div 
                  className="detail-card glow-on-hover"
                  whileHover={{ y: -5 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="detail-icon pulse-animation">
                    <i className="fas fa-envelope"></i>
                  </div>
                  <div className="detail-content">
                    <h4>Email</h4>
                    <a 
                      href="mailto:asmadachathushki@gmail.com" 
                      className="detail-value link-hover"
                      onClick={(e) => e.stopPropagation()}
                    >
                      asmadachathushki@gmail.com
                    </a>
                    <div className="detail-underline"></div>
                  </div>
                </motion.div>
                
                <motion.div 
                  className="detail-card glow-on-hover"
                  whileHover={{ y: -5 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="detail-icon pulse-animation">
                    <i className="fas fa-map-marker-alt"></i>
                  </div>
                  <div className="detail-content">
                    <h4>Location</h4>
                    <p className="detail-value">
                      <span className="city">Nugegoda</span>, 
                      <span className="country">Colombo, Sri Lanka</span>
                    </p>
                    <div className="detail-underline"></div>
                  </div>
                </motion.div>

                <motion.div 
                  className="detail-card glow-on-hover"
                  whileHover={{ y: -5 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="detail-icon pulse-animation">
                    <i className="fas fa-graduation-cap"></i>
                  </div>
                  <div className="detail-content">
                    <h4>Education</h4>
                    <p className="detail-value">Information Technology</p>
                    <div className="detail-underline"></div>
                  </div>
                </motion.div>
              </div>
            </motion.div>

            {/* Call to Action 
            <motion.div 
              className="cta-section"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 1 }}
            >
              <motion.button 
                className="download-cv-btn"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <i className="fas fa-download"></i>
                Download CV
              </motion.button>
              
              <motion.button 
                className="contact-btn"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <i className="fas fa-paper-plane"></i>
                Get In Touch
              </motion.button>
            </motion.div>*/}
          </motion.div>
          
          {/* Image Section */}
          <motion.div 
            className="about-image-container"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="image-wrapper">
              <div className="image-frame">
                <motion.img 
                  src="/assets/images/projects/profile2 (2).jpeg" 
                  alt="Chathushki Kumarage - Software Engineer" 
                  className="profile-image"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                  loading="lazy"
                />
                <div className="image-decoration"></div>
                <div className="image-dots"></div>
                <div className="image-border"></div>
              </div>

              {/* Floating Stats */}
              <motion.div 
                className="floating-stat stat-1"
                initial={{ scale: 0, rotate: -10 }}
                whileInView={{ scale: 1, rotate: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                {/*<div className="stat-number">2+</div>
                <div className="stat-label">Years Experience</div>*/}
              </motion.div>

              <motion.div 
                className="floating-stat stat-2"
                initial={{ scale: 0, rotate: 10 }}
                whileInView={{ scale: 1, rotate: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                {/*<div className="stat-number">10+</div>
                <div className="stat-label">Projects Done</div>*/}
              </motion.div>

              {/* Achievement Badge */}
              <motion.div 
                className="image-badge floating"
                initial={{ scale: 0, y: 20 }}
                whileInView={{ scale: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.8 }}
              >
                <div className="badge-icon">
                  <i className="fas fa-award"></i>
                </div>
                <div className="badge-content">
                  <span className="badge-title">IT Undergraduate</span>
                  <span className="badge-subtitle">Passionate Developer</span>
                </div>
              </motion.div>
            </div>

            
          </motion.div>
        </div>

        {/* Bottom Decoration */}
        <motion.div 
          className="section-bottom"
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="bottom-decoration">
            <div className="decoration-line"></div>
            <div className="decoration-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <div className="decoration-line"></div>
          </div>
        </motion.div>
      </div>
    </section>
  );
});

About.displayName = 'About';

export default About;